import Badge from "@/components/custom/badge";
import Card from "@/components/custom/card";
import Loading from "@/components/custom/loading";
import { toast } from "@/hooks/use-toast";
import { Mixpanel } from "@/lib/mixpanel";
import { MaintenanceExecutionEntry, MaintenanceTask } from "@/lib/types";
import {
  formatDate,
  getMaintenanceScheduleTypeTranslationKey,
} from "@/lib/utils";
import {
  useGetMaintenanceExecutionEntries,
  useGetTaskById,
} from "@/services/queries/maintenance";
import useAuth from "@/store/use-auth";
import useVessel from "@/store/use-vessel";
import { useHasPermission } from "@/hooks/use-permission";
import { PERMISSIONS } from "@/lib/constants/permissions";
import {
  CaretLeft,
  ClipboardText,
  UserCirclePlus,
} from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AddExecutionEntryButton from "./components/add-execution-entry-button";
import EditExecutionEntryForm from "./components/edit-execution-entry-form";
import ManageAssigneesDrawer from "./components/manage-assignees-drawer";
import { useTranslation } from "react-i18next";
import { RiCloseLargeLine, RiPencilLine } from "@remixicon/react";
import { useDeleteMaintenanceExecutionEntry } from "@/services/mutations/maintenance";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const TaskDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedVessel } = useVessel();
  const { user } = useAuth();
  const { id } = useParams();
  const { t } = useTranslation();
  const canWriteExecution = useHasPermission(
    PERMISSIONS.Maintenance.Execution.Write
  );
  const canWriteAssignment = useHasPermission(
    PERMISSIONS.Maintenance.Assignment.Write
  );
  const [assigneesDrawerOpen, setAssigneesDrawerOpen] = useState(false);
  const {
    data: task,
    isError,
    isPending,
    refetch: refetchTask,
  } = useGetTaskById({
    vesselId: selectedVessel?.id ?? "",
    id: id ?? "",
  });
  const {
    data: executionEntries,
    isPending: isExecutionEntriesPending,
    refetch: refetchExecutionEntries,
  } = useGetMaintenanceExecutionEntries({
    taskId: task?.id ?? "",
    vesselId: selectedVessel?.id ?? "",
  });

  useEffect(() => {
    if (isError) {
      navigate(
        location.state?.redirectPath ? "/" + location.state?.redirectPath : "/"
      );
      toast({
        title: t("error_message.title"),
        description: t("error_message.task_not_found_description"),
        variant: "destructive",
      });
    }
  }, [isError, location.state?.redirectPath, navigate, t]);

  const hasPermissionToAddExecutionEntry = useMemo(() => {
    return (
      canWriteExecution ||
      task?.taskDetail.assignees?.some((a) => a.id === user?.sub)
    );
  }, [user, task, canWriteExecution]);

  return (
    <>
      <title>Task Details</title>
      <div data-testid="task-detail-page" className="px-5">
        <div className="flex items-center justify-between">
          <button
            aria-label="Back button"
            onClick={() => {
              navigate(
                location.state?.redirectPath
                  ? "/" + location.state?.redirectPath
                  : "/"
              );

              Mixpanel.track("Back Button Clicked");
            }}
          >
            <CaretLeft size={20} color="#FFFFFF" />
          </button>
          <div className="flex items-center gap-3">
            {canWriteAssignment && task && selectedVessel?.id && (
              <button
                aria-label="Manage assignees"
                onClick={() => setAssigneesDrawerOpen(true)}
              >
                <UserCirclePlus size={22} color="#FFFFFF" />
              </button>
            )}
            {canWriteExecution &&
              task &&
              selectedVessel?.id &&
              hasPermissionToAddExecutionEntry && (
                <AddExecutionEntryButton
                  vesselId={selectedVessel.id}
                  taskId={task.id}
                  refetchTask={refetchTask}
                />
              )}
          </div>
        </div>

        {isPending || !task ? (
          <Loading className="mt-10" />
        ) : isError ? (
          <div className="mt-10">
            <h1 className="text-center text-2xl font-medium text-white">
              {t("error_message.task_not_found")}
            </h1>
          </div>
        ) : (
          <>
            <Header task={task} />

            {task.taskDetail.assignees?.length > 0 && (
              <div className="mt-4 px-3">
                <span className="text-sm text-white/60">
                  {t("label.assignees")}
                </span>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {task.taskDetail.assignees.map((assignee) => (
                    <span
                      key={assignee.id}
                      className="rounded-full bg-[#1FB3CC]/20 px-2.5 py-1 text-xs font-medium text-[#7ECEE8]"
                    >
                      {assignee.fullName}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {
              <div className="mt-16 space-y-5 px-3">
                {isExecutionEntriesPending ? (
                  <Loading />
                ) : executionEntries?.items?.length === 0 ? (
                  <div>
                    <h1 className="gap-4 text-center font-medium leading-5 text-white">
                      {t("error_message.no_execution_entries_found")}
                    </h1>
                  </div>
                ) : (
                  <>
                    {executionEntries?.items?.map((executionEntry) => (
                      <ExecutionEntriesCard
                        key={executionEntry.id}
                        executionEntry={executionEntry}
                        vesselId={selectedVessel?.id ?? ""}
                        taskId={task.id}
                        refetch={refetchExecutionEntries}
                        refetchTask={refetchTask}
                        isReadOnly={!canWriteExecution}
                      />
                    ))}
                  </>
                )}
              </div>
            }
          </>
        )}

        {task && selectedVessel?.id && canWriteAssignment && (
          <ManageAssigneesDrawer
            open={assigneesDrawerOpen}
            setOpen={setAssigneesDrawerOpen}
            task={task}
            vesselId={selectedVessel.id}
            onUpdated={refetchTask}
          />
        )}
      </div>
    </>
  );
};

export default TaskDetail;

const Header = ({ task }: { task: MaintenanceTask }) => {
  const { t } = useTranslation();

  return (
    <div className="mt-10 space-y-4 px-3">
      <div className="flex flex-col gap-1">
        <span className="text-base text-white/80">{t("label.task")}</span>
        <span className="text-[28px] font-semibold leading-[32px] text-white">
          {task.name}
        </span>
      </div>

      <Badge
        data-testid="task-schedule-type-badge"
        label={t(
          getMaintenanceScheduleTypeTranslationKey(task.taskDetail.scheduleType)
        )}
        variant="primary"
      />
    </div>
  );
};

const ExecutionEntriesCard = ({
  executionEntry,
  vesselId,
  taskId,
  refetch,
  refetchTask,
  isReadOnly,
}: {
  executionEntry: MaintenanceExecutionEntry;
  vesselId: string;
  taskId: string;
  refetch: () => void;
  refetchTask: () => void;
  isReadOnly: boolean;
}) => {
  const [editOpen, setEditOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { t } = useTranslation();

  const { mutate: deleteExecutionEntry, isPending: isDeleting } =
    useDeleteMaintenanceExecutionEntry({
      onSuccess: () => {
        refetch();
        setDeleteDialogOpen(false);
      },
    });

  const onDelete = () => {
    deleteExecutionEntry({
      vesselId,
      taskId,
      id: executionEntry.id,
    });
  };

  return (
    <>
      <Card
        data-testid={`execution-entry-card-${executionEntry.id}`}
        key={executionEntry.id}
      >
        <div className="flex w-full flex-row items-center justify-between">
          <div className="flex flex-row items-center gap-[14px]">
            <span className="flex items-center justify-center text-white/50">
              <ClipboardText size={20} />
            </span>

            <div className="flex flex-col gap-0.5">
              <span className="text-base font-medium leading-5 text-white">
                {executionEntry.notes}
              </span>

              <span
                data-testid={`execution-entry-hours-${executionEntry.id}`}
                className="space-x-2 text-[14px] font-medium leading-5"
              >
                <span className="text-white/80">
                  {executionEntry.executionDate
                    ? formatDate(executionEntry.executionDate, "dd/MM/yyyy")
                    : ""}
                </span>
                <span className="text-white">{"-"}</span>
                <span className="text-[#7ECEE8]">
                  {executionEntry.runningHoursAtExecution && (
                    <>
                      {executionEntry.runningHoursAtExecution}{" "}
                      {t("label.hours")}
                    </>
                  )}
                </span>
              </span>
            </div>
          </div>

          {!isReadOnly && (
            <div className="flex flex-row items-center gap-3">
              <button
                onClick={() => {
                  setEditOpen(true);
                }}
              >
                <RiPencilLine color="#ffffff" size={16} />
              </button>
              <RiCloseLargeLine
                data-testid={`delete-execution-entry-button-${executionEntry.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteDialogOpen(true);
                }}
                color="#ffffff"
                size={16}
              />
            </div>
          )}
        </div>
      </Card>

      {!isReadOnly && (
        <EditExecutionEntryForm
          open={editOpen}
          setOpen={setEditOpen}
          executionEntry={executionEntry}
          vesselId={vesselId}
          taskId={taskId}
          refetch={refetch}
          refetchTask={refetchTask}
        />
      )}

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader className="text-left">
            <DialogTitle>{t("title.delete_execution_entry")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-start justify-center gap-[10px]">
            <p className="text-white">
              {t("label.delete_execution_entry_description")}
            </p>
            <div className="flex w-full flex-row items-center justify-end gap-[10px]">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                className="w-full"
              >
                {t("button.cancel")}
              </Button>
              <Button
                className="w-full"
                onClick={onDelete}
                disabled={isDeleting}
              >
                {t("button.delete")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
